import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, Optional, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { PrivateService } from 'app/core/services/private.service';
import { HRConcept } from 'app/shared/models/hr-concept';
import { PayrollPeriod } from 'app/shared/models/payroll-period';
import { ToastrService } from 'ngx-toastr';
import { Observable, map } from 'rxjs';

@Component({
  selector: 'add-leave-dialog',
  templateUrl: './add-leave-dialog.component.html',
  styleUrls: ['./add-leave-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddLeaveDialogComponent implements OnInit {

  stepperOrientation: StepperOrientation = 'horizontal';
  payrollPeriod: PayrollPeriod[] = [];
  hrConcept: HRConcept[] = [];
  employees: any[] = [];
  selectedEmployeeBPartnerId: string = '';

  // Filtered arrays
  filteredEmployees: any[] = [];
  filteredPayrollPeriods: PayrollPeriod[] = [];
  filteredHRConcepts: HRConcept[] = [];

  // Search text properties
  employeeSearchText: string = '';
  periodSearchText: string = '';
  leaveTypeSearchText: string = '';

  EmployeeGroup: FormGroup;
  PeriodGroup: FormGroup;
  DateGroup: FormGroup;
  HRConceptGroup: FormGroup;

  minDate!: Date;
  maxDate!: Date;

  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('employeeSearchInput') employeeSearchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('periodSearchInput') periodSearchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('leaveTypeSearchInput') leaveTypeSearchInput!: ElementRef<HTMLInputElement>;

  constructor(
    private formBuilder: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private privateService: PrivateService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<AddLeaveDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: { employeeId?: string } | null
  ) {
    this.EmployeeGroup = this.formBuilder.group({
      employeeID: ['', Validators.required],
    });

    this.PeriodGroup = this.formBuilder.group({
      payrollPeriodID: ['', Validators.required],
    });

    this.DateGroup = this.formBuilder.group({
      datePick1: ['', Validators.required],
      datePick2: ['', Validators.required],
    });

    this.HRConceptGroup = this.formBuilder.group({
      hrconceptID: ['', Validators.required],
      comment: ['', Validators.required],
    });

    // Set stepper orientation based on screen size
    this.breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')))
      .subscribe(orientation => {
        this.stepperOrientation = orientation as StepperOrientation;
      });
  }

  ngOnInit(): void {
    this.getEmployees();
    this.getHRConcept();
  }

  // Filter methods
  filterEmployees(): void {
    if (!this.employeeSearchText) {
      this.filteredEmployees = [...this.employees];
    } else {
      const searchLower = this.employeeSearchText.toLowerCase();
      this.filteredEmployees = this.employees.filter(employee =>
        employee.employeeName.toLowerCase().includes(searchLower)
      );
    }
  }

  filterPeriods(): void {
    if (!this.periodSearchText) {
      this.filteredPayrollPeriods = [...this.payrollPeriod];
    } else {
      const searchLower = this.periodSearchText.toLowerCase();
      this.filteredPayrollPeriods = this.payrollPeriod.filter(period =>
        period.payrollName.toLowerCase().includes(searchLower)
      );
    }
  }

  filterLeaveTypes(): void {
    if (!this.leaveTypeSearchText) {
      this.filteredHRConcepts = [...this.hrConcept];
    } else {
      const searchLower = this.leaveTypeSearchText.toLowerCase();
      this.filteredHRConcepts = this.hrConcept.filter(concept =>
        concept.conceptName.toLowerCase().includes(searchLower)
      );
    }
  }

  // Dropdown open/close handlers
  onEmployeeDropdownOpenChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => {
        this.employeeSearchInput?.nativeElement.focus();
      }, 100);
    } else {
      this.employeeSearchText = '';
      this.filterEmployees();
    }
  }

  onPeriodDropdownOpenChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => {
        this.periodSearchInput?.nativeElement.focus();
      }, 100);
    } else {
      this.periodSearchText = '';
      this.filterPeriods();
    }
  }

  onLeaveTypeDropdownOpenChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => {
        this.leaveTypeSearchInput?.nativeElement.focus();
      }, 100);
    } else {
      this.leaveTypeSearchText = '';
      this.filterLeaveTypes();
    }
  }

  getEmployees() {
    this.privateService.getEmployeeInfoAll().subscribe({
      next: res => {
        this.employees = res;
        this.filteredEmployees = res;
        if (this.dialogData?.employeeId) {
          const id = this.dialogData.employeeId;
          this.EmployeeGroup.patchValue({ employeeID: id });
          this.selectedEmployeeBPartnerId = id;
          this.getPeriod(id);
        }
      },
      error: err => {
        console.log(err);
        this.showNotificationDanger('Failed to load employees');
      }
    });
  }

  onEmployeeChange(employeeBPartnerId: string) {
    console.log('Selected employee C_BPartner_ID:', employeeBPartnerId);
    this.selectedEmployeeBPartnerId = employeeBPartnerId;

    // Reset period and date selections when employee changes
    this.PeriodGroup.patchValue({ payrollPeriodID: '' });
    this.DateGroup.patchValue({ datePick1: null, datePick2: null });
    this.payrollPeriod = [];
    this.filteredPayrollPeriods = [];
    this.minDate = null;
    this.maxDate = null;

    // Load periods for the selected employee
    if (employeeBPartnerId) {
      this.getPeriod(employeeBPartnerId);
    }
  }

  getPeriod(C_BPartner_ID: string) {
    // Use getPeriodForEmployee which implements the PERIOD_CONFIG range logic
    this.privateService.getPeriodBP(C_BPartner_ID).subscribe({
      next: response => {
        this.payrollPeriod = response;
        this.filteredPayrollPeriods = response;
        console.log('Payroll periods loaded for employee:', response);

        if (response.length === 0) {
          this.showNotificationDanger('No available periods found for this employee');
        }
      },
      error: err => {
        console.log(err);
        this.showNotificationDanger('Failed to load payroll periods');
      }
    });
  }

  getHRConcept() {
    this.privateService.getHRConcept().subscribe({
      next: response => {
        this.hrConcept = response;
        this.filteredHRConcepts = response;
      },
      error: err => {
        console.log(err);
        this.showNotificationDanger('Failed to load leave types');
      }
    });
  }

  onPeriodChange(selectedID: string) {
    const selectedPayroll = this.payrollPeriod.find(p => p.hrperiodID === selectedID);

    if (!selectedPayroll) {
      console.error("Error: Payroll period not found for ID", selectedID);
      return;
    }

    const [start, end] = selectedPayroll.payrollName.split(" To ");

    this.minDate = new Date(start);
    this.maxDate = new Date(end);

    this.minDate.setHours(0, 0, 0, 0);
    this.maxDate.setHours(23, 59, 59, 999);

    console.log("Period selected - Min Date:", this.minDate, "Max Date:", this.maxDate);

    // Reset date fields if they're outside the new range
    const datePick1 = this.DateGroup.get('datePick1')?.value;
    const datePick2 = this.DateGroup.get('datePick2')?.value;

    if (datePick1 && (datePick1 < this.minDate || datePick1 > this.maxDate)) {
      this.DateGroup.patchValue({ datePick1: null });
    }

    if (datePick2 && (datePick2 < this.minDate || datePick2 > this.maxDate)) {
      this.DateGroup.patchValue({ datePick2: null });
    }

    this.cdr.detectChanges();
  }

  dateFilter = (d: Date | null): boolean => {
    if (!d || !this.minDate || !this.maxDate) return true;
    return d >= this.minDate && d <= this.maxDate;
  };

  submitLeaveForm() {
    if (!this.isFormValid()) {
      this.showNotificationDanger('Please fill in all required fields');
      return;
    }

    // Get the date values and convert to ISO string format
    const datePick1 = this.DateGroup.get('datePick1')?.value;
    const datePick2 = this.DateGroup.get('datePick2')?.value;

    const formData = {
      employeeID: this.EmployeeGroup.value.employeeID,
      periodGroup: this.PeriodGroup.value,
      dateGroup: {
        datePick1: datePick1 instanceof Date ? datePick1.toISOString() : datePick1,
        datePick2: datePick2 instanceof Date ? datePick2.toISOString() : datePick2
      },
      hrconceptGroup: this.HRConceptGroup.value
    };

    console.log('Submitting leave form:', formData);

    this.privateService.submitLeaveFormForEmployee(formData).subscribe({
      next: res => {
        if (res.success) {
          this.showNotificationSuccess(res.message);
          this.dialogRef.close(true);
        } else {
          this.showNotificationDanger(res.message);
        }
      },
      error: err => {
        console.log('Error response:', err);
        const errorMessage = err.error?.message || 'Error submitting leave request';
        this.showNotificationDanger(errorMessage);
      }
    });
  }

  isFormValid(): boolean {
    return this.EmployeeGroup.valid &&
      this.PeriodGroup.valid &&
      this.DateGroup.valid &&
      this.HRConceptGroup.valid;
  }

  getSelectedEmployeeName(): string {
    const employeeID = this.EmployeeGroup.get('employeeID')?.value;
    if (!employeeID) return 'Not selected';
    const employee = this.employees.find(e => e.C_BPartner_ID === employeeID);
    return employee ? employee.employeeName : 'Not selected';
  }

  getSelectedPeriodName(): string {
    const periodID = this.PeriodGroup.get('payrollPeriodID')?.value;
    if (!periodID) return 'Not selected';
    const period = this.payrollPeriod.find(p => p.hrperiodID === periodID);
    return period ? period.payrollName : 'Not selected';
  }

  getSelectedLeaveType(): string {
    const leaveTypeID = this.HRConceptGroup.get('hrconceptID')?.value;
    if (!leaveTypeID) return 'Not selected';

    // Try both property names in case the API returns different formats
    const leaveType = this.hrConcept.find(h =>
      h.hrconceptID === leaveTypeID || h['hr_concept_id'] === leaveTypeID
    );

    console.log('Leave Type ID:', leaveTypeID);
    console.log('Found Leave Type:', leaveType);
    console.log('All HR Concepts:', this.hrConcept);

    return leaveType ? leaveType.conceptName : 'Not selected';
  }

  showNotificationSuccess(text: string) {
    this.toastr.success('<span class="now-ui-icons ui-1_bell-53"></span> ' + text, '', {
      timeOut: 3000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-success alert-with-icon",
      positionClass: 'toast-top-center'
    });
  }

  showNotificationDanger(text: string) {
    this.toastr.error('<span class="now-ui-icons ui-1_bell-53"></span> ' + text, '', {
      timeOut: 3000,
      closeButton: true,
      enableHtml: true,
      toastClass: "alert alert-danger alert-with-icon",
      positionClass: 'toast-top-center'
    });
  }
}